import { PawnState } from '@dreamgame/shared';
import { signal } from '@preact/signals';

// ============================================================================
// Command Queue - Client-side tracking of shift-queued commands
// ============================================================================

export interface QueuedCommand {
  readonly commandType: number;
  readonly targetX: number;
  readonly targetY: number;
  readonly targetEntity: number;
}

const MAX_QUEUE_SIZE = 20;

// Storage: pawn network ID -> queued commands
const queueMap = new Map<number, QueuedCommand[]>();

// Version signal for reactive consumers (render layer)
export const queueVersion = signal(0);

const notifyQueueChanged = (): void => {
  queueVersion.value = queueVersion.value + 1;
};

// Add a command to the queue for each pawn
export const enqueueCommands = ({
  pawnIds,
  command,
}: {
  pawnIds: ReadonlyArray<number>;
  command: QueuedCommand;
}): void => {
  for (const pawnId of pawnIds) {
    let queue = queueMap.get(pawnId);
    if (!queue) {
      queue = [];
      queueMap.set(pawnId, queue);
    }
    if (queue.length < MAX_QUEUE_SIZE) {
      queue.push(command);
    }
  }
  notifyQueueChanged();
};

// Clear queues for given pawns (called on non-queue commands)
export const clearQueue = ({ pawnIds }: { pawnIds: ReadonlyArray<number> }): void => {
  let changed = false;
  for (const pawnId of pawnIds) {
    if (queueMap.has(pawnId)) {
      queueMap.delete(pawnId);
      changed = true;
    }
  }
  if (changed) {
    notifyQueueChanged();
  }
};

// Clear a single pawn's queue (cancel, death, removal)
export const clearQueueForPawn = ({ pawnNetworkId }: { pawnNetworkId: number }): void => {
  if (queueMap.has(pawnNetworkId)) {
    queueMap.delete(pawnNetworkId);
    notifyQueueChanged();
  }
};

// Dequeue first item when pawn transitions from IDLE to active state
export const syncPawnState = ({
  pawnNetworkId,
  prevState,
  newState,
}: {
  pawnNetworkId: number;
  prevState: number;
  newState: number;
}): void => {
  if (prevState !== PawnState.IDLE || newState === PawnState.IDLE) {
    return;
  }

  const queue = queueMap.get(pawnNetworkId);
  if (!queue || queue.length === 0) {
    return;
  }

  queue.shift();
  if (queue.length === 0) {
    queueMap.delete(pawnNetworkId);
  }
  notifyQueueChanged();
};

// Get queue array for a pawn (returns empty array if none)
export const getQueue = ({
  pawnNetworkId,
}: {
  pawnNetworkId: number;
}): ReadonlyArray<QueuedCommand> => {
  return queueMap.get(pawnNetworkId) ?? [];
};

// Clear all queues (disconnect cleanup)
export const clearAllQueues = (): void => {
  if (queueMap.size > 0) {
    queueMap.clear();
    notifyQueueChanged();
  }
};
